import { useState, useEffect, useCallback } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { DEFAULT_AVATAR_ID } from "../../../lib/avatars/index.js";

const NAME_KEY = "coscratch_student_name";
const COLOR_KEY = "coscratch_student_color";
const AVATAR_KEY = "coscratch_student_avatar";

const randomStudentColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const [r, g, b] = [0, 8, 4].map((offset) => {
        const k = (offset + hue / 30) % 12;
        const channel =
            0.62 - 0.38 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(channel * 255);
    });
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
};

export const useAuth = (db) => {
    const [teacherUser, setTeacherUser] = useState(null);
    const [teacherName, setTeacherName] = useState("");
    const [studentName, setStudentName] = useState("");
    const [studentColor, setStudentColorState] = useState(randomStudentColor);
    const [studentAvatar, setStudentAvatarState] = useState(DEFAULT_AVATAR_ID);
    const [teacherAvatar, setTeacherAvatar] = useState(null);
    const [savingAvatar, setSavingAvatar] = useState(false);
    const [adminUser, setAdminUser] = useState(false);
    const [loginError, setLoginError] = useState("");

    useEffect(() => {
        const saved = localStorage.getItem(NAME_KEY);
        if (saved) setStudentName(saved);

        const savedColor = localStorage.getItem(COLOR_KEY);
        if (savedColor) setStudentColorState(savedColor);

        const savedAvatar = localStorage.getItem(AVATAR_KEY);
        if (savedAvatar) setStudentAvatarState(savedAvatar);
    }, []);

    const setStudentColor = useCallback((color) => {
        setStudentColorState(color);
        localStorage.setItem(COLOR_KEY, color);
    }, []);

    const setStudentAvatar = useCallback((avatar) => {
        setStudentAvatarState(avatar);
        localStorage.setItem(AVATAR_KEY, avatar);
    }, []);

    useEffect(() => {
        const unsuscribe = db.auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    const data = await db.getTeacherData(user.uid);
                    setTeacherUser(user);
                    const fullName = `${data.name || ""} ${data.surname || ""}`;
                    setTeacherName(fullName || user.email);
                    setTeacherAvatar(data.avatar ?? null);
                    const isUserAdmin = await db.isUserAdmin();
                    setAdminUser(isUserAdmin);
                } catch (error) {
                    console.error(error);
                    setTeacherUser(user);
                    setTeacherName(user.email);
                }
            } else {
                setTeacherUser(null);
                setTeacherName("");
                setTeacherAvatar(null);
                setAdminUser(false);
            }
        });

        return () => unsuscribe();
    }, [db]);

    useEffect(() => {
        if (!!teacherUser) {
            setStudentName("");
            localStorage.removeItem(NAME_KEY);
        }
    }, [teacherUser]);

    const handleLogin = useCallback(
        async (username, password, isTeacherMode) => {
            if (isTeacherMode) {
                try {
                    const userCredential = await signInWithEmailAndPassword(
                        db.auth,
                        username,
                        password,
                    );
                    const isUserAdmin = await db.isUserAdmin();
                    setAdminUser(isUserAdmin);
                    setLoginError("");
                    return { success: true };
                } catch (error) {
                    setLoginError("Correo o contraseña incorrectos");
                    return {
                        success: false,
                        error: "Correo o contraseña incorrectos",
                    };
                }
            } else {
                const name = username.trim();
                if (name) {
                    setStudentName(name);
                    localStorage.setItem(NAME_KEY, name);
                    setLoginError("");
                    return { success: true };
                }
                return { success: false, error: "Nombre vacío" };
            }
        },
        [db],
    );

    const handleLogout = useCallback(() => {
        try {
            if (teacherUser) {
                db.auth.signOut();
                setTeacherUser(null);
                setAdminUser(false);
            } else {
                setStudentName("");
                localStorage.removeItem(NAME_KEY);
            }
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [db, teacherUser]);

    const saveTeacherAvatar = useCallback(
        async (avatar) => {
            setSavingAvatar(true);
            try {
                await db.updateTeacherAvatar(avatar);
                setTeacherAvatar(avatar);
                return { success: true };
            } catch (error) {
                return { success: false, error: error.message };
            } finally {
                setSavingAvatar(false);
            }
        },
        [db],
    );

    const currentUser = teacherName || studentName;
    const isLoggedIn = !!currentUser;
    const currentAvatar = teacherUser ? teacherAvatar : studentAvatar;

    return {
        teacherUser,
        teacherName,
        studentName,
        studentColor,
        studentAvatar,
        teacherAvatar,
        currentAvatar,
        savingAvatar,
        adminUser,
        loginError,
        setLoginError,
        currentUser,
        isLoggedIn,
        handleLogin,
        handleLogout,
        setStudentName,
        setStudentColor,
        setStudentAvatar,
        saveTeacherAvatar,
    };
};
